import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { twElementStyles } from '../../../../twElementStyles'
import { RenderStatus } from '../../common'
import { CustomTable } from '../../../../components/customTable'
import { FilterOptions } from '../../../../components/customTable/custom-table-interfaces'
import { SmoothTransition } from '../../../../components/pageTransition'
import { useForm } from 'react-hook-form'
import { getErrorResponse, getTimeRelativeTime, getUpdatedSearchParams, parseSearchParams } from '../../../../services/util'
import { usePopup } from '../../../../context/popupContext'
import { useNotificationBar } from '../../../../context/notificationContext'
import { useOTTUsersData, useUpdateOTTUser, useUpdateOTTUserTestStatus } from '../../../../hooks/ott-user'
import { PagePreloader } from '../../../../components/pagePreloader'
import { Unauthorized } from '../../../Unauthorized'

export const EndUsersList = () => {

    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: any = parseSearchParams(searchParams, 'filter')

    const useFormHook = useForm()
    let popup = usePopup()
    const notification = useNotificationBar()
    const rowSelectionState = useState<RowSelectionState>({})
    let { data, isFetching: isLoading, refetch, isError, isSuccess, error, dataUpdatedAt } = useOTTUsersData(filterParams, {
        keepPreviousData: true
    })
    let { mutate: updateUser } = useUpdateOTTUser()
    let { mutate: updateTestUserStatus } = useUpdateOTTUserTestStatus()

    const onStatusChange = async (row: any) => {
        let update: any = { is_active: row.is_active ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.is_active ? 'Activate' : 'Deactivate'} user ${row.name} ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateUser({
                        data: update,
                        id: row.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `User ${update.is_active ? 'activation success !' : 'deactivated !'} `,
                                message: `User ${row.first_name} ${row.last_name} ${update.is_active ? 'activated' : 'deactivated'} successfully !`,
                                type: update.is_active ? 'success' : 'info'
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            }
        })
    }

    const onTestUserStatusChange = async (row: any) => {
        let update: any = { is_test_user: row.is_test_user ? 0 : 1 }
        popup.open({
            title: `Are you sure ?`,
            message: `Do you want to ${update.is_test_user ? 'Mark' : 'Unmark'} user ${row.name} as test user ?`,
            type: 'confirm',
            onAction: (value: any) => {
                if (value) {
                    updateTestUserStatus({
                        data: update,
                        id: row.id
                    }, {
                        onSuccess: () => {
                            notification.push({
                                title: `Test user status updated !`,
                                message: `User ${row.name} ${update.is_test_user ? 'marked as test user' : 'unmarked as test user'} successfully !`,
                                type: update.is_test_user ? 'success' : 'info'
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        }
                    })
                }
            }
        })
    }

    const columns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            header: 'NAME',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'email',
            header: () => 'EMAIL',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'mobile',
            header: () => 'CONTACT NO',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'gender',
            header: () => 'GENDER',
            footer: (info) => info.column.id,
        },
        {
            accessorKey: 'created_date',
            header: 'Member Since',
            footer: (info) => info.column.id,
            cell: ({ row }: any) => (
                <>
                    {new Date(row.original.created_date).toLocaleDateString()}
                </>
            ),
        },
        {
            id: 'is_active',
            header: 'STATUS',
            accessorKey: 'is_active',
            cell: ({ row }) => RenderStatus(row, 'is_active', onStatusChange),
            enableSorting: false
        },
        {
            id: 'is_test_user',
            header: 'TEST USER',
            accessorKey: 'is_test_user',
            cell: ({ row }) => RenderStatus(row, 'is_test_user', onTestUserStatusChange),
            enableSorting: false
        },
    ]

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(filterParams, values, name)
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                }
                else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    const filterOptions: FilterOptions[] = [{
        key: 'is_active',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Active`,
                value: 1
            },
            {
                display: `Inactive`,
                value: 0
            }
        ],
        label: 'Status'
    },
    {
        key: 'is_test_user',
        options: [
            {
                display: `All`,
                value: ''
            },
            {
                display: `Test User`,
                value: 1
            },
            {
                display: `Normal User`,
                value: 0
            }
        ],
        label: 'Test User'
    }]

    const mobileViewOptions = {
        mainTitle: {
            accessKey: 'email'
        }
    }

    const responseData = data?.data ?? {
        results: [],
        pages: 0,
        total: 0
    };

    return (
        (isError || isSuccess) ?
            <SmoothTransition>
                {
                    (error as any)?.response?.data?.statusCode === 403 ?
                        <Unauthorized /> :
                        <div className='w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md '>
                            <aside className="flex flex-auto items-center">
                                <CustomTable
                                    title="OTT Users Management"
                                    titleLabel={`Last updated on ${getTimeRelativeTime(Date.now())}`}
                                    formHook={useFormHook}
                                    columns={columns}
                                    data={responseData.results}
                                    pages={responseData.pages}
                                    totalRecords={responseData.total}
                                    onRefresh={refetch}
                                    isLoading={isLoading}
                                    isErrored={isError}
                                    rowSelectionState={rowSelectionState}
                                    mobileViewOptions={mobileViewOptions}
                                    useFieldForSelect="id"
                                    filterOptions={filterOptions}
                                />
                            </aside>
                        </div>
                }
            </SmoothTransition>
            : <PagePreloader />
    )
}

export default EndUsersList
